import { z } from 'zod';
import { DICTIONARY } from '@/config/dictionary';
import { formatUsd } from '@/features/payments/utils/format-usd';
import type { TopUpLimits } from '@/types/platform-settings';

export const tcapSettingsSchema = z.object({
  tcapUrl: z
    .string()
    .trim()
    .min(1, DICTIONARY.dashboard.wholesalerSettings.tcapSettings.portalAddressRequired),
});
export type TcapSettingsFormValues = z.infer<typeof tcapSettingsSchema>;

/**
 * `null` is the empty box. Modelling it explicitly stops a cleared field from being read
 * as `0`, which would silently save a zero threshold.
 */
const usdAmount = (emptyMessage: string) =>
  z
    .number()
    .nullable()
    // TS narrows `value` to `number` for the second refine off the first one's predicate.
    .refine((value) => value !== null, { message: emptyMessage })
    .refine((value) => value >= 0, {
      message: DICTIONARY.common.validation.amountNotNegative,
    });

/**
 * A top-up amount held to the platform's configured floor and ceiling.
 *
 * `limits` is nullable because the limits are fetched: until they arrive the only rule that
 * can be enforced is "greater than zero". Guessing a range here would reject amounts the
 * platform actually allows.
 */
const topUpAmount = (limits: TopUpLimits | null) => {
  const positive = z
    .number()
    .nullable()
    .refine((value) => value !== null && value > 0, {
      message: DICTIONARY.common.validation.amountPositive,
    });

  if (limits === null) return positive;

  return positive
    .refine((value) => value === null || value >= limits.minTopUpUsd, {
      message: DICTIONARY.common.validation.amountBelowMin(formatUsd(limits.minTopUpUsd)),
    })
    .refine((value) => value === null || value <= limits.maxTopUpUsd, {
      message: DICTIONARY.common.validation.amountAboveMax(formatUsd(limits.maxTopUpUsd)),
    });
};

export const creditThresholdsFormSchema = z.object({
  warningLevelUsd: usdAmount(DICTIONARY.common.validation.amountRequired),
  restrictionLevelUsd: usdAmount(DICTIONARY.common.validation.amountRequired),
  topUpAmountUsd: usdAmount(DICTIONARY.common.validation.amountRequired),
});
export type CreditThresholdsFormValues = z.infer<typeof creditThresholdsFormSchema>;

/**
 * Same shape as {@link creditThresholdsFormSchema}, but the auto top-up amount is held to
 * the platform's top-up limits — an automatic charge is a top-up and the backend rejects it
 * on the same bounds as a manual one.
 */
export const creditThresholdsFormSchemaFor = (limits: TopUpLimits | null) =>
  z.object({
    warningLevelUsd: usdAmount(DICTIONARY.common.validation.amountRequired),
    restrictionLevelUsd: usdAmount(DICTIONARY.common.validation.amountRequired),
    topUpAmountUsd: topUpAmount(limits),
  });

export const topUpAmountSchemaFor = (limits: TopUpLimits | null) =>
  z.object({ amount: topUpAmount(limits) });
export type TopUpAmountFormValues = z.infer<ReturnType<typeof topUpAmountSchemaFor>>;

/**
 * Whether an amount is inside the platform's top-up range. Mirrors
 * {@link topUpAmountSchemaFor} for the call sites that need a boolean to disable a button
 * rather than a parsed result.
 */
export const isWithinTopUpLimits = (amount: number, limits: TopUpLimits | null): boolean => {
  if (amount <= 0) return false;
  if (limits === null) return true;
  return amount >= limits.minTopUpUsd && amount <= limits.maxTopUpUsd;
};
